import React from 'react';

import { Header } from './header';

import { RiveAnimation } from '@/components/RiveAnimation';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import { useColors } from '@/hooks/useColors';

/**
 * Transition Screen Message Types
 *
 * @description
 * Defines the message types that can be displayed in the transition screen.
 * Supports both static predefined messages and dynamic status updates.
 */
type TransitionScreenMessage =
  | 'Securely authenticating your account ...'
  | 'Submitting your inquiry ...'
  | string;

/**
 * Transition Screen Component
 *
 * @overview
 * Displays a loading screen with animated logo and status message during app transitions.
 * Used for authentication, inquiry submission, and other async operations.
 *
 * @behavior
 * - Shows the animated Primum logo
 * - Displays static or dynamic status messages
 * - Supports both predefined messages and real-time status updates
 * - Provides visual feedback during long-running operations
 *
 * @props
 * - message: Status text to display (supports dynamic updates)
 */
function TransitionScreen({ message }: { message: TransitionScreenMessage }) {
  const { colors } = useColors();

  return (
    <VStack className="flex-1 items-center justify-center overflow-hidden px-5">
      <Header headerShown={false} statusBarBackgroundColor={colors.background[0]} />

      <VStack className="z-10 mb-10 h-24 w-full">
        <RiveAnimation name="PRIMUM_LOGO" />
      </VStack>

      <Text className="z-0 text-center">{message}</Text>
    </VStack>
  );
}

export default TransitionScreen;
